import { FC, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Table,
  TextLink,
  type IndicatorAlgorithmType,
} from '@atlas-frontend/sdk';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ReportIndicatorAlgorithmsTable.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'report-indicator-algorithms-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<IndicatorAlgorithmType>();

interface ReportIndicatorAlgorithmsTableProps {
  dataObjectDescriptionVersionId: number;
  algorithms: IndicatorAlgorithmType[];
  onAlgorithmCodeClick?: (
    algorithmCode: string,
    targetSequenceNumber: number | null,
  ) => void;
}

export const ReportIndicatorAlgorithmsTable: FC<
  ReportIndicatorAlgorithmsTableProps
> = (props) => {
  const { dataObjectDescriptionVersionId, algorithms, onAlgorithmCodeClick } =
    props;
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('algorithmCode', {
        header: t('column.algorithmCode.header'),
        cell: (props) => {
          const algorithmCode = props.getValue();
          if (onAlgorithmCodeClick) {
            return (
              <TextLink
                id={`indicator-algorithm-${algorithmCode}`}
                onClick={() =>
                  onAlgorithmCodeClick(
                    algorithmCode,
                    props.row.original.algorithmTargetSequenceNumber,
                  )
                }
              >
                {algorithmCode}
              </TextLink>
            );
          }
          return (
            <UiLink
              id={`indicator-algorithm-${algorithmCode}`}
              path={path.outputDataObjectView}
              params={{
                id: dataObjectDescriptionVersionId,
                segment: 'algorithms',
                algorithmCode,
              }}
              target="_blank"
            >
              {algorithmCode}
            </UiLink>
          );
        },
      }),
      columnHelper.accessor(
        (rowData) =>
          t(`column.confidentialityRule.${rowData.confidentialityRule}`),
        {
          id: 'confidentialityRule',
          header: t('column.confidentialityRule.header'),
        },
      ),
      columnHelper.accessor('dimension', {
        header: t('column.dimension.header'),
      }),
      columnHelper.accessor('algorithmTargetSequenceNumber', {
        header: t('column.algorithmTargetSequenceNumber.header'),
        cell: (props) => props.renderValue(),
      }),
    ],
    [t, dataObjectDescriptionVersionId, onAlgorithmCodeClick],
  );

  return (
    <Table
      name="indicator-algorithms"
      columns={columns}
      data={algorithms || EMPTY_DATA}
    />
  );
};

const EMPTY_DATA: any[] = [];
